import { ReactNode, useMemo } from 'react';

import { ScreenSize, useScreenSize } from '@/hooks/useScreenSize';
import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_MOBILE } from '@/utils/constants';

import clsx from 'clsx';

type BackgroundWrapperProps = {
  isBackgroundCoverNavigationBar?: boolean;
  isFormExtended: boolean;
  children: ReactNode;
};

function getBaseHeight(screenSize: number) {
  if (screenSize > ScreenSize.LG) {
    return '100%';
  }
  if (screenSize > ScreenSize.MD) {
    return 'calc(100% - 500px)';
  }
  return 'calc(100% - 300px)';
}

function addNavbarHeight(height: string, screenSize: number) {
  return `calc(${height} + ${
    screenSize <= ScreenSize.SM ? NAVBAR_HEIGHT_MOBILE : NAVBAR_HEIGHT
  }px)`;
}

function removeFormExtension(height: string) {
  return `calc(${height} - 300px)`;
}

type GetHeightProps = {
  screenSize: number;
  isBackgroundCoverNavigationBar?: boolean;
  isFormExtended: boolean;
};

function getHeight({
  screenSize,
  isBackgroundCoverNavigationBar,
  isFormExtended,
}: GetHeightProps) {
  let height = getBaseHeight(screenSize);

  if (isBackgroundCoverNavigationBar) {
    height = addNavbarHeight(height, screenSize);
  }

  if (isFormExtended && screenSize < ScreenSize.MD) {
    height = removeFormExtension(height);
  }

  return height;
}

export function BackgroundWrapper({
  isBackgroundCoverNavigationBar,
  children,
  isFormExtended,
}: BackgroundWrapperProps) {
  const screenSize = useScreenSize();

  const height = useMemo(
    () =>
      getHeight({ screenSize, isBackgroundCoverNavigationBar, isFormExtended }),
    [screenSize, isBackgroundCoverNavigationBar, isFormExtended]
  );

  return (
    <div
      className={clsx(
        'absolute left-0 right-0 w-full z-[-2] h-full min-h-[320px]',
        isBackgroundCoverNavigationBar
          ? `top-[-${NAVBAR_HEIGHT_MOBILE}px] sm:top-[-${NAVBAR_HEIGHT}px]`
          : 'top-0'
      )}
      style={{
        height,
      }}
    >
      {children}
    </div>
  );
}
